/**
 * Hash-based router matching the original AgentMeeting Full.html shell.
 * Routes:
 *   landing | pricing | docs | changelog
 *   signup | signin | auth/verify
 *   settings | billing
 *   app/agenda | app/room | app/studio | app/replay | app/resolution | app/meetings
 */
import React from 'react';
import { Landing } from './pages/landing';
import { Pricing } from './pages/pricing';
import { Docs } from './pages/docs';
import { Changelog } from './pages/changelog';
import { Auth } from './pages/auth';
import { Settings } from './pages/settings';
import { Billing } from './pages/billing';
import { AgendaEditor } from './pages/agenda';
import { RoomChatStream } from './pages/room-a';
import { RoomRoundTable } from './pages/room-b';
import { RoomArena } from './pages/room-c';
import { Studio } from './pages/studio';
import { Replay } from './pages/replay';
import { ResolutionCenter } from './pages/resolution';
import { MeetingsHistory } from './pages/meetings';
import { Admin } from './pages/admin';
import { TopBar, RoomLayout } from './shared/topbar';
import { Role } from './shared/ui';
import { useAuth } from './shared/auth';
import { meetings as meetingsApi } from './shared/api';

const APP_PREFS_KEY = 'agentmeeting.prefs';
type Prefs = {
  theme: 'dark' | 'light';
  meetingType: 'debate' | 'decision' | 'consult';
  userRole: Role;
  roomLayout: RoomLayout;
};
const DEFAULT_PREFS: Prefs = {
  theme: 'dark',
  meetingType: 'decision',
  userRole: 'participant',
  roomLayout: 'chat-stream',
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(APP_PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch { return DEFAULT_PREFS; }
}

function useHashRoute(initial: string) {
  const get = () => {
    const h = (location.hash || '').replace(/^#\/?/, '').split('?')[0];
    return h || initial;
  };
  const [route, setRoute] = React.useState<string>(get());
  React.useEffect(() => {
    const onHash = () => setRoute(get());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const goTo = React.useCallback((r: string) => {
    location.hash = '#/' + r;
    window.scrollTo(0, 0);
  }, []);
  return [route, goTo] as const;
}

export function App() {
  const { user, loading } = useAuth();
  const [prefs, setPrefs] = React.useState<Prefs>(loadPrefs);
  const [route, goTo] = useHashRoute('landing');
  const [role, setRole] = React.useState<Role>(prefs.userRole);

  const setPref = <K extends keyof Prefs>(key: K, value: Prefs[K]) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem(APP_PREFS_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  React.useEffect(() => {
    document.documentElement.dataset.theme = prefs.theme;
  }, [prefs.theme]);
  React.useEffect(() => { setRole(prefs.userRole); }, [prefs.userRole]);

  const [section, ...rest] = route.split('/');
  const sub = rest.join('/');

  // /app/* requires auth; redirect to /signin once auth state is resolved.
  React.useEffect(() => {
    if (section === 'app' && !loading && !user) {
      goTo('signin');
    }
  }, [section, loading, user, goTo]);

  // /admin requires auth (in addition to the server-side admin gate).
  React.useEffect(() => {
    if (section === 'admin' && !loading && !user) {
      goTo('signin');
    }
  }, [section, loading, user, goTo]);

  // Already-authenticated users hitting /signin or /signup get bumped to the
  // app. This is more reliable than per-form post-submit redirects which
  // can be swallowed by React state batching during the form's submit cycle.
  React.useEffect(() => {
    if (!loading && user && (section === 'signin' || section === 'signup' || section === 'login')) {
      window.location.replace('/#/app/meetings');
    }
  }, [section, loading, user]);

  // Load the current meeting for the TopBar (title, tokens, spend, round, status).
  const [appMeeting, setAppMeeting] = React.useState<any>(null);
  React.useEffect(() => {
    if (section !== 'app' || !user) { setAppMeeting(null); return; }
    const mid = localStorage.getItem('agentmeeting.currentMeetingId');
    if (!mid) { setAppMeeting(null); return; }
    meetingsApi.get(mid).then(setAppMeeting).catch(() => setAppMeeting(null));
  }, [section, user, route]);

  if ((section === 'app' || section === 'admin') && loading) {
    return <div className="frame" style={{ height: '100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div className="mono dim sm">loading…</div>
    </div>;
  }
  if ((section === 'app' || section === 'admin') && !user) {
    return null; // useEffect above will redirect
  }

  let page: React.ReactNode;
  if (section === 'landing' || route === '') {
    page = <Landing goTo={goTo} />;
  } else if (section === 'pricing') {
    page = <Pricing goTo={goTo} />;
  } else if (section === 'docs') {
    page = <Docs goTo={goTo} anchor={sub} />;
  } else if (section === 'changelog') {
    page = <Changelog goTo={goTo} />;
  } else if (section === 'signup') {
    page = <Auth goTo={goTo} mode="signup" />;
  } else if (section === 'signin' || section === 'login') {
    page = <Auth goTo={goTo} mode="signin" />;
  } else if (section === 'auth') {
    page = <Auth goTo={goTo} mode={sub || 'signin'} />;
  } else if (section === 'onboarding') {
    // Onboarding wizard removed — legacy URLs land on the meetings page where
    // an inline "create new meeting" form is shown to fresh users.
    goTo('app/meetings');
    page = null;
  } else if (section === 'settings') {
    page = <Settings goTo={goTo} />;
  } else if (section === 'billing') {
    page = <Billing goTo={goTo} />;
  } else if (section === 'admin') {
    page = <Admin goTo={goTo} />;
  } else if (section === 'app') {
    const appRoute = sub || 'agenda';
    const topBar = (
      <TopBar
        here={appRoute}
        goTo={(r: string) => goTo('app/' + r)}
        role={role}
        onRoleChange={(r: Role) => { setRole(r); setPref('userRole', r); }}
        meetingType={prefs.meetingType}
        roomLayout={prefs.roomLayout}
        onLayoutChange={(v) => setPref('roomLayout', v)}
        meeting={appMeeting}
      />
    );
    if (appRoute === 'room') {
      if (prefs.roomLayout === 'round-table')
        page = <RoomRoundTable role={role} meetingType={prefs.meetingType} embedded topBar={topBar} />;
      else if (prefs.roomLayout === 'arena')
        page = <RoomArena role={role} meetingType={prefs.meetingType} embedded topBar={topBar} />;
      else
        page = <RoomChatStream role={role} meetingType={prefs.meetingType} embedded topBar={topBar} />;
    }
    else if (appRoute === 'studio')     page = <Studio embedded topBar={topBar} />;
    else if (appRoute === 'agenda')     page = <AgendaEditor embedded topBar={topBar} />;
    else if (appRoute === 'replay')     page = <Replay embedded topBar={topBar} />;
    else if (appRoute === 'resolution') page = <ResolutionCenter goTo={(r: string) => goTo('app/' + r)} />;
    else if (appRoute === 'meetings')   page = <MeetingsHistory goTo={goTo} />;
    else                                 page = <AgendaEditor embedded topBar={topBar} />;
  } else {
    page = <Landing goTo={goTo} />;
  }

  const isApp = section === 'app';
  return (
    <div style={{
      flex: 1,
      display: 'flex', flexDirection: 'column',
      minHeight: '100vh',
      height: isApp ? '100vh' : 'auto',
      minWidth: 0,
    }}>
      {page}
    </div>
  );
}
